import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Flex } from 'rebass';
import { Card, Button, Label } from 'semantic-ui-react';
import { Form, Input, Dropdown } from 'formsy-semantic-ui-react';
import { addValidationRule } from 'formsy-react';

import { getCurrentEducationYear } from 'Util/DateUtils';
import { thaiRegularExpressionWithOutNumber, errorLabel } from 'Util/form';
import { MAJOR } from 'Util/Constants';

import { Text } from '../Base';

const SCHOOL_FILTER_API = 'https://tcas-assets.skooldio.com/output/';
const START_STR = ['โรงเรียน', 'รร.', 'ร.ร.'];

export const normalizeQuery = (query = '') => {
  const match = START_STR.find(ss => query.startsWith(ss));
  if (match) {
    return query.substr(match.length).trim();
  }
  return query.trim();
};

const fetchAPI = async query => {
  const nQuery = normalizeQuery(query);
  const qLength = nQuery.length;

  if (qLength === 0) return null;

  const API = SCHOOL_FILTER_API + encodeURIComponent(nQuery) + '.json';

  const result = await fetch(API);

  if (result.status !== 200) {
    return [{ title: query, id: undefined, province: undefined, inter: undefined }];
  }

  const json = await result.json();

  return json;
};

const StyledCard = styled(Card)`
  &.ui.card {
    border-radius: 0;
    width: 100%;
    min-width: 320px;
  }
  &.ui.card > .content {
    ${'' /* align-items: center; */};
  }
`;

const FWButton = styled(Button)`
  width: 100%;
`;

const FWField = styled(Form.Field)`
  width: 100%;
`;

const FormBox = styled(Box)`
  max-width: 300px;
`;

const ButtonGroup = props => {
  const { items = [], selected, onChange, type } = props;
  return (
    <Button.Group fluid>
      {items.map(({ value, label }) => (
        <Button
          key={value}
          type="button"
          onClick={() => {
            onChange(type, value);
          }}
          active={selected === value}
        >
          {label}
        </Button>
      ))}
    </Button.Group>
  );
};

ButtonGroup.propTypes = {
  items: PropTypes.array,
  onChange: PropTypes.func,
  type: PropTypes.string,
};

const TFTitle = ({ title }) => {
  return (
    <Text is="label" fontWeight={400} style={{ fontWeight: 400 }}>
      {title}
    </Text>
  );
};

export const USER_TYPE = [
  { value: 'STUDENT', label: 'นักเรียน' },
  { value: 'PARENT', label: 'ผู้ปกครอง' },
  { value: 'TEACHER', label: 'ครู/อาจารย์' },
];
const SEX = [
  { value: 'MALE', label: 'ชาย' },
  { value: 'FEMALE', label: 'หญิง' },
  { value: 'OTHER', label: 'ไม่ระบุ' },
];
const GRADE = [
  { value: 'M6', text: 'ม.6' },
  { value: 'M5', text: 'ม.5' },
  { value: 'M4', text: 'ม.4' },
  { value: 'M3', text: 'ม.3' },
  { value: 'M2', text: 'ม.2' },
  { value: 'M1', text: 'ม.1' },
  { value: 'GRADE12', text: 'Grade 12 / Year 13' },
  { value: 'GRADE11', text: 'Grade 11 / Year 12' },
  { value: 'GRADE10', text: 'Grade 10 / Year 11' },
  { value: 'GRADE9', text: 'Grade 9 / Year 10' },
  { value: 'GRADE8', text: 'Grade 8 / Year 9' },
  { value: 'GRADE7', text: 'Grade 7 / Year 8' },
  { value: 'OTHER', text: 'อื่นๆ' },
];
export const MAJOR_OPTIONS = [
  { value: MAJOR.SCIENCE, text: 'วิทย์-คณิต' },
  { value: MAJOR.ART_LANG, text: 'ศิลป์-ภาษา' },
  { value: MAJOR.ART_MATH, text: 'ศิลป์-คำนวณ' },
  { value: MAJOR.OTHER, text: 'อื่นๆ' },
];
addValidationRule('isThai', function(values, value) {
  return thaiRegularExpressionWithOutNumber.test(value); // You can return an error
});

class AddPersonalInformation extends Component {
  state = {
    sex: 'MALE',
    userType: 'STUDENT',
    schoolList: [],
  };
  static propTypes = {
    onSubmit: PropTypes.func,
    mutation: PropTypes.func,
  };

  handleSchoolSearchChange = async (e, { searchQuery }) => {
    const schoolList = await fetchAPI(searchQuery);
    this.setState({
      schoolList: schoolList ? schoolList : [],
    });
  };

  updateInformation = async form => {
    const { major, grade, firstName, lastName, schoolId } = form;
    const { userType, sex } = this.state;
    const { id, province, inter, title } = this.state.schoolList.find(school =>
      school.id ? school.id === schoolId : true,
    );
    const userDetails = { firstName, lastName, sex, userType };
    const educationRecords = {
      school: title,
      schoolId: id ? id : undefined,
      province: province ? province : undefined,
      grade,
      major,
      interSchool: inter ? inter : undefined,
    };
    const year = getCurrentEducationYear();
    await this.props.mutation({
      variables: {
        userDetails: userDetails,
        educationRecord: educationRecords,
        educationYear: year,
      },
    });
    this.props.onUpdatedUserProfile();
  };
  handleButtonGroupChange = (key, value) => {
    this.setState({ [key]: value });
  };

  renderUserType() {
    return (
      <Flex justifyContent="center" alignItems="center" mb={3} flexDirection="column">
        <Text is="label" fontWeight={500} mb={2}>
          คุณคือ ?
        </Text>
        <ButtonGroup
          items={USER_TYPE}
          selected={this.state.userType}
          onChange={this.handleButtonGroupChange}
          type={'userType'}
        />
      </Flex>
    );
  }

  renderPersonalInformation() {
    return (
      <Flex justifyContent="center" alignItems="center" mb={3} flexDirection="column">
        <Text is="label" fontWeight={500} mb={2}>
          ข้อมูลส่วนตัว
        </Text>
        <FWField>
          <TFTitle title="ชื่อ (ภาษาไทย)" />
          <Input
            name="firstName"
            validations={{ isThai: true }}
            validationErrors={{
              isThai: 'กรุณากรอกชื่อของคุณเป็นภาษาไทย',
            }}
            placeholder=""
            errorLabel={errorLabel}
          />
        </FWField>
        <FWField>
          <TFTitle title="นามสกุล" />
          <Input
            name="lastName"
            validations={{ isThai: true }}
            validationErrors={{
              isThai: 'กรุณากรอกนามสกุลของคุณเป็นภาษาไทย',
            }}
            placeholder=""
            errorLabel={errorLabel}
          />
        </FWField>
        <FWField>
          <TFTitle title="เพศ" />
          <ButtonGroup
            items={SEX}
            selected={this.state.sex}
            onChange={this.handleButtonGroupChange}
            type={'sex'}
          />
        </FWField>
      </Flex>
    );
  }

  renderSchoolInformation() {
    return (
      <Flex justifyContent="center" alignItems="center" mb={4} flexDirection="column">
        <Text fontWeight={500} textAlign="center">
          กำลังศึกษาอยู่ที่
        </Text>
        <FWField>
          <TFTitle title="โรงเรียน" />
          <Dropdown
            selection
            search
            fluid
            scrolling
            noResultsMessage={null}
            onSearchChange={this.handleSchoolSearchChange}
            options={this.state.schoolList.map(school => {
              const { id, title } = school;

              return { text: title, key: id || title, value: id || title };
            })}
            name="schoolId"
            required
            validationErrors={{
              isDefaultRequiredValue: 'กรุณาใส่ชื่อโรงเรียนของคุณ',
            }}
            errorLabel={errorLabel}
          />
        </FWField>

        <FWField>
          <TFTitle title="ระดับชั้น" />
          <Dropdown
            selection
            name="grade"
            options={GRADE}
            errorLabel={errorLabel}
            validations="isExisty"
            validationError="กรุณาเลือกระดับชั้น"
          />
        </FWField>

        <FWField>
          <TFTitle title="แผนการเรียน" />
          <Dropdown
            selection
            validations="isExisty"
            validationError="กรุณาเลือกแผนการเรียน"
            name="major"
            options={MAJOR_OPTIONS}
            errorLabel={errorLabel}
          />
        </FWField>
      </Flex>
    );
  }

  render() {
    return (
      <StyledCard>
        <Card.Content>
          <Form onValidSubmit={this.updateInformation}>
            <FormBox pb={3} mx="auto" pt={3}>
              <Flex justifyContent="center" alignItems="center">
                <Box>
                  {this.renderUserType()}
                  {this.renderPersonalInformation()}
                  {this.renderSchoolInformation()}
                  <FWButton
                    primary
                    circular
                    size="medium"
                    type="submit"
                    content={<span style={{ fontWeight: 400 }}>อัพเดทข้อมูล</span>}
                  />
                </Box>
              </Flex>
            </FormBox>
          </Form>
        </Card.Content>
      </StyledCard>
    );
  }
}

export default AddPersonalInformation;
